import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  TrendingUp,
  Megaphone,
  HeadphonesIcon,
  Wallet,
  Scale,
  Settings,
  Code2,
  Users,
} from 'lucide-react'
import { Container } from '../layout/Container'
import { Card } from '../ui/Card'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'

interface LandingArea {
  id: string
  title: string
  description: string
  icon: string
  metric: string
  metricLabel: string
}

const iconMap: Record<string, React.ElementType> = {
  sales: TrendingUp,
  marketing: Megaphone,
  customerService: HeadphonesIcon,
  finance: Wallet,
  legal: Scale,
  operations: Settings,
  software: Code2,
  hr: Users,
}

export function SolutionAreas() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const areas: LandingArea[] = t('solutionAreas.items', { returnObjects: true }) as LandingArea[]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  return (
    <section id="areas" className="py-24">
      <Container>
        <div className="mb-16 text-center">
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('solutionAreas.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold text-text-primary md:text-4xl">
            {t('solutionAreas.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-text-secondary">
            {t('solutionAreas.description')}
          </p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {areas.map((area) => {
            const Icon = iconMap[area.icon] || Settings
            return (
              <motion.div key={area.id} variants={itemVariants}>
                <Card className="h-full">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent-light">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-text-primary">{area.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary">{area.description}</p>
                  <div className="mt-6 border-t border-border pt-4">
                    <div className="text-lg font-semibold text-gradient">{area.metric}</div>
                    <div className="text-xs text-text-muted">{area.metricLabel}</div>
                  </div>
                </Card>
              </motion.div>
            )
          })}
        </motion.div>
      </Container>
    </section>
  )
}
